import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Hand } from "lucide-react";
import { engine } from "@/lib/drumAudio";

const BPM = 90;
const LISTEN = 4;
const TAPS = 4;

// Plays 4 listening clicks then 4 clicks to tap along with; the average gap
// between the scheduled click and the drummer's tap becomes the Sync value.
export const TapSync = ({ open, onOpenChange, onResult }) => {
  const [phase, setPhase] = useState("idle");
  const [beat, setBeat] = useState(0);
  const [result, setResult] = useState(null);
  const clicksRef = useRef([]);
  const tapsRef = useRef([]);
  const timersRef = useRef([]);

  const clear = () => { timersRef.current.forEach(clearTimeout); timersRef.current = []; };
  useEffect(() => () => clear(), []);
  useEffect(() => { if (!open) { clear(); setPhase("idle"); setBeat(0); setResult(null); } }, [open]);

  const finish = () => {
    const offs = tapsRef.current
      .map((t) => {
        const nearest = clicksRef.current.slice(LISTEN).reduce((a, c) => (Math.abs(c - t) < Math.abs(a - t) ? c : a), Infinity);
        return t - nearest;
      })
      .filter((d) => Math.abs(d) < 0.5);
    if (offs.length < 2) { setPhase("failed"); return; }
    offs.sort((a, b) => a - b);
    const median = offs[Math.floor(offs.length / 2)];
    const lat = Math.min(0.12, (engine.ctx && (engine.ctx.outputLatency || engine.ctx.baseLatency)) || 0);
    const ms = Math.max(-500, Math.min(500, Math.round(((lat - median) * 1000) / 5) * 5));
    setResult(ms);
    setPhase("done");
  };

  const start = () => {
    engine.ensure();
    if (engine.ctx.state === "suspended") engine.ctx.resume();
    clear();
    tapsRef.current = [];
    const beatDur = 60 / BPM;
    const t0 = engine.now + 0.3;
    clicksRef.current = Array.from({ length: LISTEN + TAPS }, (_, i) => t0 + i * beatDur);
    clicksRef.current.forEach((t, i) => {
      engine.click(t, i % 4 === 0);
      timersRef.current.push(setTimeout(() => { setBeat(i + 1); if (i === LISTEN) setPhase("tap"); }, (t - engine.now) * 1000));
    });
    timersRef.current.push(setTimeout(finish, (t0 + (LISTEN + TAPS) * beatDur + 0.3 - engine.now) * 1000));
    setPhase("listen");
    setBeat(0);
    setResult(null);
  };

  const tap = (e) => {
    if (e) e.preventDefault();
    if (phase !== "tap" && !(phase === "listen" && beat >= LISTEN)) return;
    tapsRef.current.push(engine.now);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.code === "Space" || e.key === " ") { e.preventDefault(); e.stopPropagation(); tap(); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0b0c10] border-white/10 text-slate-100 max-w-md w-[calc(100%-1.5rem)] rounded-xl" data-testid="tap-sync-modal">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight">Tap to sync</DialogTitle>
          <DialogDescription className="text-slate-400">
            Listen to 4 clicks, then tap the big button (or press Space) exactly on the next 4 clicks. Use the speakers or headphones you play with.
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-center gap-3 my-2" data-testid="tap-sync-lights">
          {Array.from({ length: LISTEN + TAPS }).map((_, i) => (
            <span
              key={i}
              className={`w-5 h-5 rounded-full transition-colors duration-75 ${
                beat === i + 1 ? (i < LISTEN ? "bg-[#00f0ff]" : "bg-[#ffab00]") : beat > i ? (i < LISTEN ? "bg-[#00f0ff]/30" : "bg-[#ffab00]/30") : "bg-white/10"
              }`}
            />
          ))}
        </div>
        <p className="text-center text-sm font-tab text-slate-300 min-h-[1.5rem]" data-testid="tap-sync-status">
          {phase === "idle" && "Ready when you are."}
          {phase === "listen" && "Listen…"}
          {phase === "tap" && "Tap now!"}
          {phase === "failed" && "Didn't catch enough taps — try again."}
          {phase === "done" && `Sync set to ${result > 0 ? "+" : ""}${result}ms`}
        </p>
        {phase === "listen" || phase === "tap" ? (
          <button
            onPointerDown={tap}
            className={`h-32 rounded-2xl border-2 flex flex-col items-center justify-center gap-2 select-none touch-none transition-colors ${phase === "tap" ? "border-[#ffab00] bg-[#ffab00]/10 text-[#ffab00]" : "border-white/15 text-slate-500"}`}
            data-testid="tap-sync-pad"
          >
            <Hand className="w-8 h-8" />
            <span className="font-heading uppercase tracking-widest">Tap</span>
          </button>
        ) : (
          <div className="flex gap-2">
            <Button onClick={start} className="flex-1 bg-[#00f0ff] text-black hover:bg-[#33f4ff] font-semibold" data-testid="tap-sync-start">
              {phase === "idle" ? "Start" : "Try again"}
            </Button>
            {phase === "done" && (
              <Button onClick={() => { onResult(result); onOpenChange(false); }} className="flex-1 bg-[#ffab00] text-black hover:bg-[#ffc233] font-semibold" data-testid="tap-sync-apply">
                Use this
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
